import React, { useState, useEffect, useCallback } from 'react';
import { CollectionSchemaBuilder, CollectionItemsList, type CollectionItem, type CollectionField, cmsApi, type CollectionRecord } from '..';
import { editorApi, type SiteRecord } from '../../editor';
import { Globe, Database, Plus, Sparkles, Folder } from 'lucide-react';

const webbuilderApi = { ...editorApi, ...cmsApi };

export function CollectionsPage(): React.JSX.Element {
  const [sites, setSites] = useState<SiteRecord[]>([]);
  const [activeSiteId, setActiveSiteId] = useState<string>('');
  const [collections, setCollections] = useState<CollectionRecord[]>([]);
  const [activeCollectionId, setActiveCollectionId] = useState<string>('');
  const [items, setItems] = useState<CollectionItem[]>([]);

  // New Collection Modal State
  const [showCreateCollectionModal, setShowCreateCollectionModal] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [newCollectionSlug, setNewCollectionSlug] = useState('');

  // New Item Modal State
  const [showCreateItemModal, setShowCreateItemModal] = useState(false);
  const [newItemSlug, setNewItemSlug] = useState('');

  // Load sites
  useEffect(() => {
    (async () => {
      try {
        const data = await webbuilderApi.listSites();
        if (Array.isArray(data) && data.length > 0) {
          setSites(data);
          setActiveSiteId(data[0].id);
        }
      } catch {
        // Fallback
      }
    })();
  }, []);

  // Load collections when site changes
  const loadCollections = useCallback(async (siteId: string) => {
    if (!siteId) return;
    try {
      const data = await webbuilderApi.listCollections(siteId);
      if (Array.isArray(data)) {
        setCollections(data);
        if (data.length > 0) {
          setActiveCollectionId(data[0].id);
        } else {
          setActiveCollectionId('');
          setItems([]);
        }
      }
    } catch {
      // Fallback
    }
  }, []);

  useEffect(() => {
    if (activeSiteId) {
      loadCollections(activeSiteId);
    }
  }, [activeSiteId, loadCollections]);

  // Load items when activeCollectionId changes
  const loadItems = useCallback(async (colId: string) => {
    if (!colId) return;
    try {
      const data = await webbuilderApi.listCollectionItems(colId);
      if (Array.isArray(data)) {
        setItems(
          data.map((item) => ({
            id: item.id,
            item_slug: item.item_slug,
            status: (item.status === 'published' ? 'published' : 'draft') as 'draft' | 'published',
            fields: item.fields_json || {},
            updated_at: item.updated_at ? new Date(item.updated_at).toLocaleDateString() : '',
          }))
        );
      }
    } catch {
      // Fallback
    }
  }, []);

  useEffect(() => {
    if (activeCollectionId) {
      loadItems(activeCollectionId);
    }
  }, [activeCollectionId, loadItems]);

  const handleCreateCollection = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeSiteId || !newCollectionName || !newCollectionSlug) return;
    try {
      const col = await webbuilderApi.createCollection(activeSiteId, {
        name: newCollectionName,
        slug: newCollectionSlug,
        schema_json: {
          fields: [
            { name: 'title', type: 'RichText', required: true },
            { name: 'slug', type: 'Slug', required: true },
            { name: 'banner', type: 'MediaAsset', required: false },
          ],
        },
      });
      setCollections((prev) => [...prev, col]);
      setActiveCollectionId(col.id);
      setShowCreateCollectionModal(false);
      setNewCollectionName('');
      setNewCollectionSlug('');
    } catch (err) {
      console.error('Failed to create collection', err);
    }
  };

  const handleCreateItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeCollectionId || !newItemSlug) return;
    try {
      const item = await webbuilderApi.createCollectionItem(activeCollectionId, {
        item_slug: newItemSlug,
        fields_json: { title: newItemSlug },
        status: 'published',
      });
      setItems((prev) => [
        ...prev,
        {
          id: item.id,
          item_slug: item.item_slug,
          status: (item.status === 'published' ? 'published' : 'draft') as 'draft' | 'published',
          fields: item.fields_json,
          updated_at: new Date().toLocaleDateString(),
        },
      ]);
      setShowCreateItemModal(false);
      setNewItemSlug('');
    } catch (err) {
      console.error('Failed to create item', err);
    }
  };

  const activeCol = collections.find((c) => c.id === activeCollectionId);
  const rawFields = (activeCol?.schema_json as any)?.fields;
  const activeFields: CollectionField[] = Array.isArray(rawFields) && rawFields.length > 0
    ? rawFields.map((f: any) => ({
        name: String(f.name || ''),
        type: String(f.type || 'RichText') as any,
        required: Boolean(f.required),
      }))
    : [];

  const handleSaveSchema = async (updatedFields: CollectionField[]) => {
    if (!activeSiteId || !activeCollectionId || !activeCol) return;
    try {
      const res = await webbuilderApi.updateCollection(activeSiteId, activeCollectionId, {
        name: activeCol.name,
        slug: activeCol.slug,
        schema_json: {
          fields: updatedFields,
        },
      });
      setCollections((prev) => prev.map((c) => (c.id === res.id ? res : c)));
    } catch (err) {
      console.error('Failed to update collection schema', err);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto flex flex-col gap-6">
      {/* Top Bar: Site selector and Collection switcher */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-card border border-border p-4 rounded-xl">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-primary" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Site:</span>
            <select
              value={activeSiteId}
              onChange={(e) => setActiveSiteId(e.target.value)}
              className="text-sm font-medium bg-background border border-border rounded-md px-3 py-1.5 text-foreground focus:outline-none"
            >
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.subdomain_slug})
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <Folder className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Collection:</span>
            {collections.length > 0 ? (
              <select
                value={activeCollectionId}
                onChange={(e) => setActiveCollectionId(e.target.value)}
                className="text-sm font-medium bg-background border border-border rounded-md px-3 py-1.5 text-foreground focus:outline-none"
              >
                {collections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.slug})
                  </option>
                ))}
              </select>
            ) : (
              <span className="text-xs text-muted-foreground">None created yet</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowCreateCollectionModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-background hover:bg-muted text-xs font-medium transition-colors min-h-[38px]"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>New Collection</span>
          </button>
          {activeCollectionId && (
            <button
              type="button"
              onClick={() => setShowCreateItemModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:opacity-90 text-xs font-medium transition-opacity min-h-[38px]"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>Add Entry</span>
            </button>
          )}
        </div>
      </div>

      {/* Main CMS Layout */}
      {collections.length > 0 ? (
        <div className="flex flex-col gap-8">
          <CollectionSchemaBuilder initialFields={activeFields} onSave={handleSaveSchema} />
          <CollectionItemsList items={items} />
        </div>
      ) : (
        <div className="text-center p-12 bg-card border border-border rounded-xl flex flex-col items-center justify-center gap-3">
          <Database className="h-10 w-10 text-muted-foreground/40" />
          <h3 className="text-base font-semibold text-foreground">No CMS Collections Found</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            Create structured dynamic collections like "Faculty Profiles", "Course Syllabi", or "Student Testimonials" to populate page templates.
          </p>
          <button
            type="button"
            onClick={() => setShowCreateCollectionModal(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 transition-opacity min-h-[44px]"
          >
            <Plus className="h-4 w-4" />
            <span>Create First Collection</span>
          </button>
        </div>
      )}

      {/* New Collection Modal */}
      {showCreateCollectionModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl max-w-md w-full p-6 shadow-2xl flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold text-foreground">Create CMS Collection</h2>
            </div>
            <form onSubmit={handleCreateCollection} className="flex flex-col gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Collection Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Faculty Directory"
                  value={newCollectionName}
                  onChange={(e) => {
                    setNewCollectionName(e.target.value);
                    if (!newCollectionSlug) {
                      setNewCollectionSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-'));
                    }
                  }}
                  className="w-full text-sm p-2 rounded-md border border-border bg-background text-foreground"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Collection Slug</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. faculty"
                  value={newCollectionSlug}
                  onChange={(e) => setNewCollectionSlug(e.target.value)}
                  className="w-full text-sm p-2 rounded-md border border-border bg-background text-foreground"
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                <button
                  type="button"
                  onClick={() => setShowCreateCollectionModal(false)}
                  className="px-4 py-2 rounded-md border border-border hover:bg-muted text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  Create Collection
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Item Modal */}
      {showCreateItemModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl max-w-md w-full p-6 shadow-2xl flex flex-col gap-4">
            <h2 className="text-lg font-semibold text-foreground">Add Entry to {activeCol?.name}</h2>
            <form onSubmit={handleCreateItem} className="flex flex-col gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Item Slug</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. dr-hc-verma-physics"
                  value={newItemSlug}
                  onChange={(e) => setNewItemSlug(e.target.value)}
                  className="w-full text-sm p-2 rounded-md border border-border bg-background text-foreground"
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                <button
                  type="button"
                  onClick={() => setShowCreateItemModal(false)}
                  className="px-4 py-2 rounded-md border border-border hover:bg-muted text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  Save Entry
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export const handle = { crumb: 'CMS & Collections' };
